import { useQuery } from '@tanstack/react-query'
import { format, formatDistanceToNow } from 'date-fns'
import {
  Activity,
  CheckCircle2,
  XCircle,
  Layout as LayoutIcon,
  Users,
  AlertTriangle,
} from 'lucide-react'
import { supabase } from '@/lib/supabase/client'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

interface HealthSnapshot {
  timestamp: string
  db: { ok: boolean; error: string | null }
  users: { total: number; new24h: number; new7d: number }
  layouts: { total: number; deleted: number; updated24h: number; active30d: number }
  recent_admin_actions: Array<{
    admin_id: string
    action: string
    target_type: string
    target_id: string
    details: Record<string, unknown>
    created_at: string
  }>
  edge_logs: {
    configured: boolean
    errors: Array<{ id?: string; timestamp?: number; event_message?: string }> | null
    error?: string
  }
}

async function fetchHealth(): Promise<HealthSnapshot> {
  const { data, error } = await supabase.functions.invoke('admin-system-health', { body: {} })
  if (error) throw new Error(String(error))
  if ((data as { error?: string })?.error) throw new Error((data as { error: string }).error)
  return data as HealthSnapshot
}

export function SystemHealthPage() {
  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['admin-system-health'],
    queryFn: fetchHealth,
    refetchInterval: 60_000,
    staleTime: 30_000,
  })

  return (
    <div>
      <PageHeader
        title="System Health"
        description={
          dataUpdatedAt
            ? `Last refreshed ${formatDistanceToNow(dataUpdatedAt, { addSuffix: true })} · auto-refresh every 60s`
            : 'Auto-refresh every 60s'
        }
      />

      {isLoading && !data ? (
        <div className="grid gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </div>
      ) : error || !data ? (
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">
            <AlertTriangle className="mx-auto mb-2 h-8 w-8" />
            <p>Failed to load health snapshot.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Database</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {data.db.ok ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                      <span className="text-2xl font-bold">OK</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-5 w-5 text-red-600" />
                      <span className="text-2xl font-bold">DOWN</span>
                    </>
                  )}
                </div>
                {data.db.error && (
                  <p className="mt-1 text-xs text-red-600">{data.db.error}</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.users.total.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  +{data.users.new24h} in 24h · +{data.users.new7d} in 7d
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium">Layouts</CardTitle>
                <LayoutIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{data.layouts.total.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">
                  {data.layouts.updated24h} touched in 24h · {data.layouts.active30d} active 30d ·
                  {' '}{data.layouts.deleted} deleted
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Edge Function Errors (last 25)</CardTitle>
            </CardHeader>
            <CardContent>
              {!data.edge_logs.configured ? (
                <p className="text-sm text-muted-foreground">
                  Not configured. Set <code>SUPABASE_ACCESS_TOKEN</code> and{' '}
                  <code>SUPABASE_PROJECT_REF</code> on the <code>admin-system-health</code>{' '}
                  function to surface logs here.
                </p>
              ) : data.edge_logs.error ? (
                <p className="text-sm text-red-600">{data.edge_logs.error}</p>
              ) : !data.edge_logs.errors || data.edge_logs.errors.length === 0 ? (
                <p className="text-sm text-muted-foreground">No 5xx responses in the recent window.</p>
              ) : (
                <ul className="space-y-2">
                  {data.edge_logs.errors.map((e, i) => (
                    <li key={e.id ?? i} className="rounded border bg-muted/30 p-2 text-xs">
                      <div className="text-muted-foreground">
                        {e.timestamp
                          ? format(new Date(Number(e.timestamp) / 1000), 'MMM d HH:mm:ss')
                          : '—'}
                      </div>
                      <div className="font-mono">{e.event_message ?? JSON.stringify(e)}</div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Recent Admin Actions</CardTitle>
            </CardHeader>
            <CardContent>
              {data.recent_admin_actions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No admin actions yet.</p>
              ) : (
                <ul className="space-y-1">
                  {data.recent_admin_actions.map((a, i) => (
                    <li key={i} className="flex items-center justify-between gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-[10px]">{a.action}</Badge>
                        <span className="text-muted-foreground">{a.target_type}</span>
                        {a.target_id && (
                          <code className="text-[10px] text-muted-foreground">
                            {a.target_id.slice(0, 8)}
                          </code>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(a.created_at), { addSuffix: true })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
